import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { DataList, DataListOption } from '@/services/mobi-api/data/types'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { IMG_URL } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { TextField } from '@mui/material'
import classNames from 'classnames'
import Image from 'next/image'
import React, { FunctionComponent, useRef } from 'react'
import ShowByGroup from './widget/showByGroup'
import ShowAll from './widget/showAll'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  onClick: (payload: CreateInvoiceReq, option?: DataListOption) => void
}

const Step1: FunctionComponent<Props> = ({ onClick }) => {
  const { data } = useMobiApi()
  const { theme } = useTheme()
  const { locale } = useLanguage()
  const { showError } = useToast()
  const { translate } = useIntl()
  const [loading, setLoading] = React.useState(false)
  const [number, setNumber] = React.useState<string>('')
  const [dataList, setDataList] = React.useState<DataList[] | undefined>()
  const [selectedGroup, setSelectedGroup] = React.useState(0)
  const [clickedOption, setClickedOption] = React.useState<DataListOption | undefined>()
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'left' | 'right'): void => {
    if (scrollRef.current != null) {
      scrollRef.current.scrollBy({
        left: direction === 'right' ? 100 : -100,
        behavior: 'smooth'
      })
    }
  }

  const fetchData = async (isdn: string): Promise<void> => {
    try {
      setLoading(true)
      const res = await data.getList({ isdn })
      if (res !== undefined) {
        if (res.list !== undefined) {
          const allOptions = res.list.flatMap((group) => group.options)
          setDataList([{ groupOrder: 1, groupNameEn: 'All', groupNameMn: 'Бүгд', options: allOptions }, ...res.list])
        }
        if (res.error !== undefined) {
          setDataList([])
          showError(locale === 'en' ? (res.cause?.messageEn ?? translate('error')) : (res.cause?.messageMn ?? translate('error')))
        }
      } else {
        showError(translate('error.isdnError'))
        setDataList([])
      }
    } catch (e) {
      setDataList([])
      showError(translate('error'))
    } finally {
      setLoading(false)
    }
  }

  const handleNumber = (value: string): void => {
    if (value.length < 9) {
      setNumber(value)
    }
    if (value.length === 8) {
      void fetchData(value)
    }
  }

  const handleNext = (): void => {
    if (clickedOption !== undefined) {
      const payload: CreateInvoiceReq = {
        isdn: number,
        products: [{
          productCode: clickedOption.code,
          productType: 'DATA',
          price: clickedOption.price
        }]
      }
      console.log('clicked item: ', clickedOption)
      onClick(payload, clickedOption)
    }
  }

  const selectPackage = (item: DataListOption): void => {
    setClickedOption(item)
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
      <div className='sticky top-0 z-50 md:p-6 p-3 md:border-b bg-surfaceInverse border-secondaryMidStroke'>
        <TextField
          value={number}
          id='outlined-basic'
          label={translate('phoneNumber')}
          variant='outlined'
          type='number'
          onChange={(e) => handleNumber(e.target.value)}
          className='w-full rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh'
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)'
            }
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        {
          dataList !== undefined && dataList.length > 0
            ? (
              <div className='relative flex items-center justify-center mt-4 md:w-96'>
                <DarkIconButton size='sm' type='text' onClick={() => handleScroll('left')} iconImg={theme === 'dark' ? IMG_URL + '/uploads/icons/chevron-left-white.svg' : IMG_URL + '/uploads/icons/chevron-left.svg'} />
                <div
                  ref={scrollRef}
                  className='flex gap-2 overflow-x-auto no-scrollbar'
                >
                  {dataList?.map((data: DataList, index: number) => (
                    <div
                      key={index}
                      className={classNames(
                        'flex-shrink-0 px-3 py-1 rounded-full',
                        selectedGroup === index
                          ? 'bg-secondary text-textInverse'
                          : 'bg-surfaceInverse text-textHigh border border-secondaryMidStroke'
                      )}
                      onClick={() => setSelectedGroup(index)}
                    >
                      <span className='text-sm font-semibold'>
                        {locale === 'en' ? data.groupNameEn.split(' ')[0] : data.groupNameMn.split(' ')[0]}
                      </span>
                    </div>
                  ))}
                </div>
                <DarkIconButton size='sm' type='text' onClick={() => handleScroll('right')} iconImg={theme === 'dark' ? IMG_URL + '/uploads/icons/chevron-right-white.svg' : IMG_URL + '/uploads/icons/chevron-right.svg'} />
              </div>
              )
            : (<></>)
        }
      </div>
      <div className='p-4 md:h-full no-scrollbar overflow-auto'>
        {loading && (
          <div className='w-full h-96 flex items-center justify-center'>
            <Loader />
          </div>
        )}
        {dataList !== undefined
          ? (
              selectedGroup === 0
                ? (
                  <ShowAll dataList={dataList} selectedGroup={selectedGroup} selectPackage={selectPackage} clickedOption={clickedOption} />
                  )
                : (
                  <ShowByGroup dataList={dataList} selectedGroup={selectedGroup} selectPackage={selectPackage} clickedOption={clickedOption} />
                  )
            )
          : (
            <div className='flex items-start gap-3 border border-[#F4A610] bg-[#FEFCF5] rounded-lg p-3 md:w-96 mt-4'>
              <Image draggable={false} width={20} height={20} src={IMG_URL + '/uploads/icons/warning.svg'} alt='!' />
              <span className='text-xs text-[#78350F] font-medium ml-2 whitespace-pre-line'>{translate('data.warning')}</span>
            </div>
            )}
      </div>
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96' />
        <DarkButton fullwidth disabled={clickedOption === undefined || number.length !== 8} onClick={handleNext}>
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}

export default Step1
